import type { Review, ReviewStatistics } from '~~/shared/globalTypes/reviews';

type BvPixel = {
  trackPageView: (data: Record<string, unknown>) => void;
  trackImpression: (data: Record<string, unknown>) => void;
  trackInView: (data: Record<string, unknown>, options: { containerId: string }) => void;
  trackViewedCGC: (data: Record<string, unknown>, options: { containerId: string }) => void;
  trackEvent: (name: string, data: Record<string, unknown>) => void;
};

type BvWindow = Window & {
  BV?: {
    pixel?: BvPixel;
  };
};

const BV_PRODUCT = 'RatingsAndReviews';

/**
 * Communicates data specific to the product page.
 * @param [$window=window] - window object for unit tests
 */
export const trackPageView = (stats: ReviewStatistics, productId: string, $window: BvWindow = globalThis.window as BvWindow) => {
  const numReviews = stats.totalReviewCount;
  const avgRating = stats.averageOverallRating;
  const percentRecommended = stats.recommendedCount / numReviews;
  const pageData = {
    bvProduct: BV_PRODUCT,
    productId,
    type: 'product',
    numReviews,
    avgRating,
    percentRecommended,
  };
  $window.BV?.pixel?.trackPageView(pageData);
};

/**
 * Communicates data specific to the product page.
 * @param [$window=window] - window object for unit tests
 */
export const trackImpression = (reviews: Review[], $window: BvWindow = globalThis.window as BvWindow) => {
  reviews.forEach((review) => {
    const impressionData = {
      bvProduct: BV_PRODUCT,
      productId: review.productId,
      contentId: review.reviewId,
      contentType: 'review',
    };
    $window.BV?.pixel?.trackImpression(impressionData);
  });
};

/**
 * Tracks an event with the provided review and container ID.
 * Merged both [trackInView] and [trackViewedCGC] since they
 *  take the same params and should execute at same time.
 * @param [$window=window] - window object for unit tests
 */
export const trackInViewAndViewedUCG = (review: Review, containerId: string, $window: BvWindow = globalThis.window as BvWindow) => {
  const inViewData = {
    bvProduct: BV_PRODUCT,
    productId: review.productId,
  };
  $window.BV?.pixel?.trackInView(inViewData, { containerId });
  $window.BV?.pixel?.trackViewedCGC(inViewData, { containerId });
};

/**
 * Tracks an event with the provided name, product ID and 2 optional detail fields.
 * @param [$window=window] - window object for unit tests
 */
export const trackEvent = (name: string, productId: string, detail1 = '', detail2 = '', $window: BvWindow = globalThis.window as BvWindow) => {
  $window.BV?.pixel?.trackEvent('Feature', {
    type: 'Used',
    name,
    productId,
    bvProduct: BV_PRODUCT,
    detail1,
    detail2,
  });
};
